/**
 * Tiny embed loader (published as `dist/embed.js`).
 *
 * The widget itself (React, the chat UI, socket client, ...) lives in a separate,
 * code-split ES module (`dist/embed/core.js`). This loader only reads the script
 * tag's `data-*` attributes and fetches that module when it is actually needed, so
 * adding the snippet to a page costs ~1 KB instead of several hundred KB of
 * render-competing JavaScript. That keeps Core Web Vitals (and therefore SEO) intact.
 *
 * Attributes handled here:
 * - `data-load`:     `idle` (default) | `load` | `interaction` | `immediate`
 * - `data-core-src`: override the URL of the core module (self-hosting / testing)
 */
import { DEFAULT_LOAD_STRATEGY, parseLoadStrategy, scheduleLoad } from './core/utils/schedule-load';

type EmbedCoreModule = { mountEmbed: (scriptTag: Element | null) => void };

const CORE_PATH_IN_PACKAGE = 'dist/embed/core.js';

function resolveCoreUrl(script: HTMLScriptElement): string {
  const override = script.getAttribute('data-core-src');
  if (override) return new URL(override, document.baseURI).href;

  const url = new URL(script.src, document.baseURI);
  const isFile = /\.m?js$/i.test(url.pathname);

  if (isFile) {
    // .../dist/embed.js  ->  .../dist/embed/core.js
    return new URL('embed/core.js' + url.search, url.href).href;
  }

  // Package URL as used with CDNs, e.g. https://unpkg.com/@rqmii_immersive/agent-react-sdk@5.7.0
  url.pathname = `${url.pathname.replace(/\/+$/, '')}/${CORE_PATH_IN_PACKAGE}`;
  return url.href;
}

function whenBodyReady(callback: () => void): void {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', callback, { once: true });
  } else {
    callback();
  }
}

function start(): void {
  // Must be read synchronously: `document.currentScript` is null inside callbacks.
  const script = (document.currentScript || document.getElementById('rqmii-agent-script')) as HTMLScriptElement | null;
  if (!script) {
    console.error('[Rqmii] Could not find the embed script tag. Make sure it has id="rqmii-agent-script".');
    return;
  }

  const strategy = parseLoadStrategy(script.getAttribute('data-load'), DEFAULT_LOAD_STRATEGY);

  let coreUrl: string;
  try {
    coreUrl = resolveCoreUrl(script);
  } catch (error) {
    console.error('[Rqmii] Could not resolve the widget bundle URL.', error);
    return;
  }

  scheduleLoad(strategy, () => {
    whenBodyReady(() => {
      import(/* @vite-ignore */ coreUrl)
        .then((core: EmbedCoreModule) => core.mountEmbed(script))
        .catch((error) => {
          console.error(`[Rqmii] Failed to load the widget from ${coreUrl}.`, error);
        });
    });
  });
}

start();
